
import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from './AuthContext';
import { PersonalityResult, QuizAnswer, QuizContextType, Question } from '@/types/quiz';
import { questions } from '@/data/quizQuestions';
import { calculateResults, saveResultsToSupabase } from '@/utils/quizCalculations';

// Create context
const QuizContext = createContext<QuizContextType | undefined>(undefined);

// Create provider
export const QuizProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [personalityResult, setPersonalityResult] = useState<PersonalityResult | null>(null);

  // Load quiz state from localStorage on initial render
  useEffect(() => {
    const savedQuizState = localStorage.getItem('quizState');
    if (savedQuizState) {
      try {
        const { currentStep, answers, quizCompleted } = JSON.parse(savedQuizState);
        setCurrentStep(currentStep);
        setAnswers(answers);
        setQuizCompleted(quizCompleted);
      } catch (e) {
        console.error("Error parsing saved quiz state:", e);
        localStorage.removeItem('quizState');
      }
    }

    // Try to load quiz results from localStorage
    const savedResults = localStorage.getItem('quizResults');
    if (savedResults) {
      try {
        const results = JSON.parse(savedResults);
        setPersonalityResult(results);
        console.log("Loaded results from localStorage:", results);
      } catch (e) {
        console.error("Error parsing saved quiz results:", e);
        localStorage.removeItem('quizResults');
      }
    }
  }, []);

  // Save quiz state to localStorage whenever it changes
  useEffect(() => {
    const quizState = {
      currentStep,
      answers,
      quizCompleted
    };
    localStorage.setItem('quizState', JSON.stringify(quizState));
  }, [currentStep, answers, quizCompleted]);

  // Calculate and save results when quiz is completed
  useEffect(() => {
    if (quizCompleted && answers.length > 0) {
      // Generate results based on answers
      const result = calculateResults(answers);
      setPersonalityResult(result);
      
      // Save results to localStorage for persistence across sessions
      localStorage.setItem('quizResults', JSON.stringify(result));
      console.log("Calculated and saved results:", result);

      // Save results to Supabase if user is logged in
      if (user) {
        saveResultsToSupabase(user.id, result);
      }
    }
  }, [quizCompleted, user, answers]);

  const currentQuestion = questions[currentStep - 1] || null;
  const totalSteps = questions.length;
  const isFirstQuestion = currentStep === 1;
  const isLastQuestion = currentStep === totalSteps;

  // Check if current question has been answered
  const hasAnsweredCurrentQuestion = currentQuestion
    ? answers.some(answer => answer.questionId === currentQuestion.id)
    : false;

  // Navigation functions
  const nextQuestion = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      setQuizCompleted(true);
    }
  };

  const previousQuestion = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Answer handling
  const answerQuestion = (questionId: string, value: number, category: string) => {
    const existingAnswerIndex = answers.findIndex(a => a.questionId === questionId);
    
    if (existingAnswerIndex !== -1) {
      // Update existing answer
      const updatedAnswers = [...answers];
      updatedAnswers[existingAnswerIndex] = { 
        questionId, 
        value, 
        category 
      };
      setAnswers(updatedAnswers);
    } else {
      // Add new answer
      setAnswers([...answers, { questionId, value, category }]);
    }
  };

  // Reset quiz
  const resetQuiz = () => {
    setCurrentStep(1);
    setAnswers([]);
    setQuizCompleted(false);
    setPersonalityResult(null);
    localStorage.removeItem('quizState');
    localStorage.removeItem('quizResults');
    localStorage.removeItem('hasCompletedQuiz');
    localStorage.removeItem('hasViewedResults');
  };

  const value = {
    currentStep,
    totalSteps,
    currentQuestion,
    answers,
    hasAnsweredCurrentQuestion,
    isFirstQuestion,
    isLastQuestion,
    quizCompleted,
    personalityResult,
    nextQuestion,
    previousQuestion,
    answerQuestion,
    resetQuiz
  };

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
};

// Custom hook to use quiz context
export const useQuiz = (): QuizContextType => {
  const context = useContext(QuizContext);
  if (context === undefined) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};
